import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ArrowLeft, KeyRound } from "lucide-react";
import { useAuth, formatApiError, API_BASE } from "@/context/AuthContext";
import { HOME } from "@/constants/testIds";
import { toast } from "sonner";
import { ICH_LOGO } from "@/App";

const Login = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState(location.hash === "#register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let result;
      if (mode === "login") {
        result = await login(form.email, form.password);
        toast.success("Welcome back.");
      } else {
        result = await register(form.name, form.email, form.password);
        toast.success("Account created.");
      }
      navigate(result?.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Authentication failed.");
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/auth/forgot-password`, { email: forgotEmail });
      toast.success(data.message || "Reset link issued.");
      setForgotOpen(false);
      setForgotEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not request reset.");
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-6 sm:px-10 py-12 flex flex-col">
      <div className="grain" />
      <nav className="max-w-[1400px] w-full mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-12 h-12 flex items-center justify-center overflow-hidden" style={{ border: "1px solid rgba(232,226,212,0.25)" }}>
            <img src={ICH_LOGO} alt="ICH" className="w-full h-full object-contain p-1" />
          </div>
          <div className="font-label text-[#efe7d2]">Innovative Capital Holdings</div>
        </Link>
        <Link to="/" className="nav-link inline-flex items-center gap-2"><ArrowLeft size={12} /> Back to site</Link>
      </nav>

      <div className="flex-1 flex items-center justify-center py-16">
        <div className="w-full max-w-md glass p-10">
          <div className="font-label text-[rgba(232,226,212,0.55)] mb-4">Student Portal</div>
          <h1 className="font-display text-[#efe7d2] text-4xl sm:text-5xl leading-[1.05]">
            {mode === "login" ? <>Welcome <span className="font-italic-display">back.</span></> : <>Create your <span className="font-italic-display">account.</span></>}
          </h1>
          <p className="mt-4 text-[rgba(232,226,212,0.65)] text-sm">
            {mode === "login" ? "Sign in to access your purchased courses." : "Register to access courses you've purchased or were granted."}
          </p>

          {!forgotOpen ? (
            <>
              <form onSubmit={submit} className="mt-10 space-y-6">
                {mode === "register" && (
                  <div>
                    <label htmlFor="rname" className="font-label text-[rgba(232,226,212,0.55)] block mb-3">Full Name</label>
                    <input id="rname" data-testid={HOME.registerName} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={1} className="w-full bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-3 text-[#efe7d2] font-display text-xl" placeholder="Your name" />
                  </div>
                )}
                <div>
                  <label htmlFor="aemail" className="font-label text-[rgba(232,226,212,0.55)] block mb-3">Email</label>
                  <input id="aemail" type="email" data-testid={mode === "login" ? HOME.loginEmail : HOME.registerEmail} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="w-full bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-3 text-[#efe7d2] font-display text-xl" placeholder="you@email.com" />
                </div>
                <div>
                  <label htmlFor="apass" className="font-label text-[rgba(232,226,212,0.55)] block mb-3">Password</label>
                  <input id="apass" type="password" data-testid={mode === "login" ? HOME.loginPassword : HOME.registerPassword} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} className="w-full bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-3 text-[#efe7d2] font-display text-xl" placeholder={mode === "register" ? "Min 8 characters" : "Your password"} />
                </div>
                <button data-testid={mode === "login" ? HOME.loginSubmit : HOME.registerSubmit} disabled={busy} type="submit" className="btn-solid w-full justify-center disabled:opacity-60">
                  {busy ? "Please wait..." : (mode === "login" ? "Sign In" : "Create Account")} <ArrowUpRight size={14} />
                </button>
              </form>

              <div className="mt-6 flex items-center justify-between gap-4">
                {mode === "login" ? (
                  <button data-testid="forgot-password-btn" type="button" onClick={() => { setForgotOpen(true); setForgotEmail(form.email || ""); }} className="nav-link inline-flex items-center gap-2">
                    <KeyRound size={12} /> Forgot password?
                  </button>
                ) : <span />}
                <button data-testid={HOME.toggleAuthMode} onClick={() => setMode(mode === "login" ? "register" : "login")} className="nav-link">
                  {mode === "login" ? "Need an account? Register" : "Already have an account? Sign in"}
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={submitForgot} className="mt-10 space-y-6" data-testid="forgot-password-form">
              <div>
                <label htmlFor="femail" className="font-label text-[rgba(232,226,212,0.55)] block mb-3">Account email</label>
                <input id="femail" type="email" required data-testid="forgot-email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="w-full bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-3 text-[#efe7d2] font-display text-xl" placeholder="you@email.com" />
              </div>
              <p className="text-xs text-[rgba(232,226,212,0.55)] italic">
                Enter the email you signed up with. We&rsquo;ll email you a reset link in a few seconds — check your inbox (and spam folder). If you don&rsquo;t receive it within 10 minutes, email <a href="mailto:devontae@ichventures.net" className="text-[#d9b87d] hover:underline">devontae@ichventures.net</a>.
              </p>
              <div className="flex flex-wrap gap-3">
                <button data-testid="forgot-submit" disabled={forgotBusy} type="submit" className="btn-solid disabled:opacity-60">
                  {forgotBusy ? "Sending..." : "Request reset link"} <ArrowUpRight size={14} />
                </button>
                <button data-testid="forgot-cancel" type="button" onClick={() => setForgotOpen(false)} className="btn-outline">Back to sign in</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
